import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  LineElement, 
  CategoryScale, 
  LinearScale, 
  BarElement 
} from 'chart.js';
import './Statistics.css';
import { 
  FaChartLine, 
  FaEuroSign, 
  FaShoppingCart, 
  FaClock, 
  FaCreditCard,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaArrowUp,
  FaUtensils,
  FaIceCream,
  FaGlassWhiskey,
  FaHamburger
} from 'react-icons/fa';

// Enregistrement des éléments de Chart.js pour les graphiques
ChartJS.register(ArcElement, Tooltip, Legend, LineElement, CategoryScale, LinearScale, BarElement);

const Statistics = () => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    dishPercentages: {},
    mostOrderedDish: '',
    hourlyOrders: [],
    dailyRevenue: 0,
    weeklyRevenue: 0,
    monthlyRevenue: 0,
    averageDailyRevenue: 0,
    averageWeeklyRevenue: 0,
    averageMonthlyRevenue: 0,
    dailyOrders: 0,
    weeklyOrders: 0,
    monthlyOrders: 0,
    averageDailyOrders: 0,
    averageWeeklyOrders: 0,
    averageMonthlyOrders: 0,
    paymentPercentages: {}
  });

  useEffect(() => {
    const fetchStatistics = async () => {
      setLoading(true);
      try {
      // Récupérer les commandes
      const commandesSnapshot = await getDocs(collection(db, 'total_commande'));
      const commandes = commandesSnapshot.docs.map(doc => doc.data());
  
      // Calcul des statistiques des commandes (plat, heure, revenu, etc.)
      const dishPercentages = calculateDishPercentages(commandes);
      const hourlyOrders = calculateHourlyOrders(commandes);
      const dailyRevenue = calculateDailyRevenue(commandes);
      const weeklyRevenue = calculateWeeklyRevenue(commandes);
      const monthlyRevenue = calculateMonthlyRevenue(commandes);
      const averageDailyRevenue = calculateAverageDailyRevenue(commandes);
      const averageWeeklyRevenue = calculateAverageWeeklyRevenue(commandes);
      const averageMonthlyRevenue = calculateAverageMonthlyRevenue(commandes);
      const dailyOrders = calculateDailyOrders(commandes);
      const weeklyOrders = calculateWeeklyOrders(commandes);
      const monthlyOrders = calculateMonthlyOrders(commandes);
      const averageDailyOrders = calculateAverageDailyOrders(commandes);
      const averageWeeklyOrders = calculateAverageWeeklyOrders(commandes);
      const averageMonthlyOrders = calculateAverageMonthlyOrders(commandes);
  
      // Récupérer les paiements
      const paiementSnapshot = await getDocs(collection(db, 'paiement'));
      const paiementData = paiementSnapshot.docs.map(doc => doc.data());

      // Calcul des pourcentages de paiement
      const totalPayments = paiementData.reduce((sum, paiement) => {
        return sum + paiement.cb_comptoir + paiement.espece + paiement.mobile + paiement.ticket_resto;
      }, 0);

      const cbComptoir = paiementData.reduce((sum, paiement) => sum + paiement.cb_comptoir, 0);
      const espece = paiementData.reduce((sum, paiement) => sum + paiement.espece, 0);
      const mobile = paiementData.reduce((sum, paiement) => sum + paiement.mobile, 0);
      const ticketResto = paiementData.reduce((sum, paiement) => sum + paiement.ticket_resto, 0);

      // Calcul des pourcentages
      const paymentPercentages = {
        cb_comptoir: ((cbComptoir / totalPayments) * 100).toFixed(2),
        espece: ((espece / totalPayments) * 100).toFixed(2),
        mobile: ((mobile / totalPayments) * 100).toFixed(2),
        ticket_resto: ((ticketResto / totalPayments) * 100).toFixed(2),
      };

      // Mise à jour des statistiques avec les pourcentages de paiement
      setStatistics({
        dishPercentages,
        hourlyOrders,
        dailyRevenue,
        weeklyRevenue,
        monthlyRevenue,
        averageDailyRevenue,
        averageWeeklyRevenue,
        averageMonthlyRevenue,
        dailyOrders,
        weeklyOrders,
        monthlyOrders,
        averageDailyOrders,
        averageWeeklyOrders,
        averageMonthlyOrders,
          paymentPercentages,
      });
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchStatistics();

    const intervalId = setInterval(() => {
      fetchStatistics();
    }, 24 * 60 * 60 * 1000); // Rafraîchissement tous les jours
  
    return () => clearInterval(intervalId);
  }, []); 
  
  useEffect(() => {
    const fetchStatistics = async () => {
      // même logique que précédemment...
    };
  
    // Rafraîchir les statistiques toutes les 24 heures
    const dailyIntervalId = setInterval(() => {
      fetchStatistics();
    }, 24 * 60 * 60 * 1000); // Rafraîchissement quotidien
  
    // Rafraîchir les statistiques toutes les semaines
    const weeklyIntervalId = setInterval(() => {
      fetchStatistics();
    }, 7 * 24 * 60 * 60 * 1000); // Rafraîchissement hebdomadaire
  
    // Rafraîchir les statistiques chaque mois
    const monthlyIntervalId = setInterval(() => {
      fetchStatistics();
    }, 30 * 24 * 60 * 60 * 1000); // Rafraîchissement mensuel
  
    // Nettoyer tous les intervalles au démontage du composant
    return () => {
      clearInterval(dailyIntervalId);
      clearInterval(weeklyIntervalId);
      clearInterval(monthlyIntervalId);
    };
  }, []);  

  // Calculs pour les pourcentages de plats par catégorie
  const calculateDishPercentages = (commandes) => {
    const categoryCounts = {
      entrées: {},
      plats: {},
      desserts: {},
      boissons: {}
    };

    // Fonction pour déterminer la catégorie d'un plat
    const getCategory = (dishName) => {
      const name = dishName.toLowerCase();
      if (name.includes('salade') || name.includes('tomate') || name.includes('bruschetta') || 
          name.includes('tartare') || name.includes('ceviche') || name.includes('planche')) {
        return 'entrées';
      } else if (name.includes('dessert') || name.includes('fondant') || name.includes('pavlova') || 
                 name.includes('gaufre') || name.includes('pain perdu') || name.includes('chocolat chaud')) {
        return 'desserts';
      } else if (name.includes('eau') || name.includes('coca') || name.includes('jus')) {
        return 'boissons';
      } else {
        return 'plats';
      }
    };

    const totalDishes = commandes.reduce((total, commande) => {
      return total + commande.items.reduce((subTotal, item) => {
        const category = getCategory(item.nom);
        categoryCounts[category][item.nom] = (categoryCounts[category][item.nom] || 0) + item.quantity;
        return subTotal + item.quantity;
      }, 0);
    }, 0);

    const percentages = {};
    for (const [category, dishes] of Object.entries(categoryCounts)) {
      for (const [dish, count] of Object.entries(dishes)) {
        percentages[dish] = {
          percentage: ((count / totalDishes) * 100).toFixed(2),
          category: category,
          count: count
        };
      }
    }
    return percentages;
  };

  const calculateHourlyOrders = (commandes) => {
    const hourlyCounts = Array(14).fill(0); // De 10h à 23h inclus (14 heures)
  
    commandes.forEach(commande => {
      const orderHour = new Date(commande.date.toDate()).getHours();
      if (orderHour >= 10 && orderHour <= 23) {
        hourlyCounts[orderHour - 10] += 1; // Décale pour correspondre à l'index 0
      }
    });
  
    const totalOrders = commandes.length;
    return hourlyCounts.map(count => ((count / totalOrders) * 100).toFixed(2));
  };
  
  const calculateDailyRevenue = (commandes) => {
    const today = new Date();
    return commandes.reduce((total, commande) => {
      const orderDate = new Date(commande.date.toDate());
      if (
        orderDate.getDate() === today.getDate() &&
        orderDate.getMonth() === today.getMonth() &&
        orderDate.getFullYear() === today.getFullYear()
      ) {
        return total + commande.total;
      }
      return total;
    }, 0);
  };

  const calculateWeeklyRevenue = (commandes) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    return commandes.reduce((total, commande) => {
      const orderDate = new Date(commande.date.toDate());
      if (orderDate >= startOfWeek && orderDate <= today) {
        return total + commande.total;
      }
      return total;
    }, 0);
  };

  const calculateMonthlyRevenue = (commandes) => {
    const today = new Date();
    return commandes.reduce((total, commande) => {
      const orderDate = new Date(commande.date.toDate());
      if (
        orderDate.getMonth() === today.getMonth() &&
        orderDate.getFullYear() === today.getFullYear()
      ) {
        return total + commande.total;
      }
      return total;
    }, 0);
  };

  const calculateAverageDailyRevenue = (commandes) => {
    const dailyRevenues = {};

    commandes.forEach(commande => {
      const date = commande.date.toDate();
      const day = date.getDate();
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

      if (!dailyRevenues[monthYear]) {
        dailyRevenues[monthYear] = {};
      }

      if (!dailyRevenues[monthYear][day]) {
        dailyRevenues[monthYear][day] = 0;
      }

      dailyRevenues[monthYear][day] += commande.total;
    });

    const totalDays = Object.values(dailyRevenues).reduce((sum, month) => sum + Object.keys(month).length, 0);
    const totalRevenue = Object.values(dailyRevenues).reduce((sum, month) => {
      return sum + Object.values(month).reduce((monthSum, daily) => monthSum + daily, 0);
    }, 0);

    return totalDays > 0 ? (totalRevenue / totalDays).toFixed(2) : 0;
  };

  const calculateAverageWeeklyRevenue = (commandes) => {
    const weeklyRevenues = {};
    const today = new Date();
    const yearStart = new Date(today.getFullYear(), 0, 1);

    commandes.forEach(commande => {
      const date = commande.date.toDate();
      const week = Math.ceil((((date - yearStart) / 86400000) + yearStart.getDay() + 1) / 7);
      const year = date.getFullYear();
      const yearWeek = `${year}-${week}`;

      if (!weeklyRevenues[yearWeek]) {
        weeklyRevenues[yearWeek] = 0;
      }

      weeklyRevenues[yearWeek] += commande.total;
    });

    const totalWeeks = Object.keys(weeklyRevenues).length;
    const totalRevenue = Object.values(weeklyRevenues).reduce((sum, revenue) => sum + revenue, 0);

    return totalWeeks > 0 ? (totalRevenue / totalWeeks).toFixed(2) : 0;
  };

  const calculateAverageMonthlyRevenue = (commandes) => {
    const monthlyRevenues = {};

    commandes.forEach(commande => {
      const date = commande.date.toDate();
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

      if (!monthlyRevenues[monthYear]) {
        monthlyRevenues[monthYear] = 0;
      }

      monthlyRevenues[monthYear] += commande.total;
    });

    const totalMonths = Object.keys(monthlyRevenues).length;
    const totalRevenue = Object.values(monthlyRevenues).reduce((sum, revenue) => sum + revenue, 0);

    return totalMonths > 0 ? (totalRevenue / totalMonths).toFixed(2) : 0;
  };

  const calculateDailyOrders = (commandes) => {
    const today = new Date();
    return commandes.reduce((total, commande) => {
      const orderDate = new Date(commande.date.toDate());
      return orderDate.getDate() === today.getDate() &&
        orderDate.getMonth() === today.getMonth() &&
        orderDate.getFullYear() === today.getFullYear()
        ? total + 1
        : total;
    }, 0);
  };

  const calculateWeeklyOrders = (commandes) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    return commandes.reduce((total, commande) => {
      const orderDate = new Date(commande.date.toDate());
      return orderDate >= startOfWeek && orderDate <= today
        ? total + 1
        : total;
    }, 0);
  };

  const calculateMonthlyOrders = (commandes) => {
    const today = new Date();
    return commandes.reduce((total, commande) => {
      const orderDate = new Date(commande.date.toDate());
      if (
        orderDate.getMonth() === today.getMonth() &&
        orderDate.getFullYear() === today.getFullYear()
      ) {
        return total + 1;
      }
      return total;
    }, 0);
  };

  const calculateAverageDailyOrders = (commandes) => {
    const dailyOrders = {};

    commandes.forEach(commande => {
      const date = commande.date.toDate();
      const day = date.getDate();
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

      if (!dailyOrders[monthYear]) {
        dailyOrders[monthYear] = {};
      }

      if (!dailyOrders[monthYear][day]) {
        dailyOrders[monthYear][day] = 0;
      }

      dailyOrders[monthYear][day] += 1;
    });

    const totalDays = Object.values(dailyOrders).reduce((sum, month) => sum + Object.keys(month).length, 0);
    const totalOrders = Object.values(dailyOrders).reduce((sum, month) => {
      return sum + Object.values(month).reduce((monthSum, daily) => monthSum + daily, 0);
    }, 0);

    return totalDays > 0 ? (totalOrders / totalDays).toFixed(2) : 0;
  };

  const calculateAverageWeeklyOrders = (commandes) => {
    const weeklyOrders = {};
    const today = new Date();
    const yearStart = new Date(today.getFullYear(), 0, 1);

    commandes.forEach(commande => {
      const date = commande.date.toDate();
      const week = Math.ceil((((date - yearStart) / 86400000) + yearStart.getDay() + 1) / 7);
      const year = date.getFullYear();
      const yearWeek = `${year}-${week}`;

      if (!weeklyOrders[yearWeek]) {
        weeklyOrders[yearWeek] = 0;
      }

      weeklyOrders[yearWeek] += 1;
    });

    const totalWeeks = Object.keys(weeklyOrders).length;
    const totalOrders = Object.values(weeklyOrders).reduce((sum, orders) => sum + orders, 0);

    return totalWeeks > 0 ? (totalOrders / totalWeeks).toFixed(2) : 0;
  };

  const calculateAverageMonthlyOrders = (commandes) => {
    const monthlyOrders = {};

    commandes.forEach(commande => {
      const date = commande.date.toDate();
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

      if (!monthlyOrders[monthYear]) {
        monthlyOrders[monthYear] = 0;
      }

      monthlyOrders[monthYear] += 1;
    });

    const totalMonths = Object.keys(monthlyOrders).length;
    const totalOrders = Object.values(monthlyOrders).reduce((sum, orders) => sum + orders, 0);

    return totalMonths > 0 ? (totalOrders / totalMonths).toFixed(2) : 0;
  };

  // Données pour les graphiques par catégorie
  const getCategoryData = (category) => {
    const categoryDishes = Object.entries(statistics.dishPercentages)
      .filter(([_, data]) => data.category === category)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10); // Top 10 par catégorie

    return {
      labels: categoryDishes.map(([name, _]) => name),
    datasets: [
      {
          label: 'Nombre de commandes',
          data: categoryDishes.map(([_, data]) => data.count),
          backgroundColor: category === 'entrées' ? 'rgba(59, 130, 246, 0.8)' :
                          category === 'plats' ? 'rgba(16, 185, 129, 0.8)' :
                          category === 'desserts' ? 'rgba(139, 92, 246, 0.8)' :
                          'rgba(245, 158, 11, 0.8)',
          borderColor: category === 'entrées' ? 'rgb(59, 130, 246)' :
                      category === 'plats' ? 'rgb(16, 185, 129)' :
                      category === 'desserts' ? 'rgb(139, 92, 246)' :
                      'rgb(245, 158, 11)',
          borderWidth: 2,
      },
    ],
  };
  };

  const entréesData = getCategoryData('entrées');
  const platsData = getCategoryData('plats');
  const dessertsData = getCategoryData('desserts');
  const boissonsData = getCategoryData('boissons');

  const hourlyData = {
    labels: Array.from({ length: 13 }, (_, i) => `${i + 11}h`),
    datasets: [
      {
        label: 'Pourcentage de commandes (%)',
        data: statistics.hourlyOrders,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };
  
  const hourlyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: {
            size: 12,
            weight: '600',
          },
          color: '#1f2937',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: { 
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            weight: '500',
          },
          color: '#6b7280',
        },
      },
      y: {
        beginAtZero: true,
        grid: { 
          color: '#f3f4f6',
          borderDash: [5, 5],
        },
        ticks: {
          font: {
            size: 11,
            weight: '500',
          },
          color: '#6b7280',
          callback: (value) => value + '%',
        },
      },
    },
  };


  const getCategoryOptions = () => {
    const isMobile = window.innerWidth < 768;
    const isSmallMobile = window.innerWidth < 480;
    const isMediumMobile = window.innerWidth < 600;
    
    return {
    responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
    plugins: {
      legend: {
        position: 'top',
          display: true,
          labels: {
            font: {
              size: isSmallMobile ? 10 : 12,
              weight: '600',
            },
            color: '#1f2937',
            padding: isSmallMobile ? 8 : 15,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: isSmallMobile ? 8 : 12,
          borderWidth: 1,
          callbacks: {
            label: (context) => {
              return `${context.label}: ${context.parsed.y || context.parsed} commandes`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          suggestedMax: function(context) {
            const max = Math.max(...context.chart.data.datasets[0].data);
            return Math.ceil(max * 1.1); // Ajoute 10% de marge en haut
          },
          ticks: {
            stepSize: 1,
            font: {
              size: isSmallMobile ? 10 : 11,
              weight: '500',
            },
            color: '#6b7280',
            maxTicksLimit: isMediumMobile ? 5 : 8,
          },
          grid: {
            color: '#f3f4f6',
            borderDash: [5, 5],
          },
        },
        x: {
          ticks: {
            font: {
              size: isSmallMobile ? 8 : isMobile ? 9 : 10,
              weight: '500',
            },
            color: '#6b7280',
            maxRotation: isSmallMobile ? 90 : isMediumMobile ? 60 : 45,
            maxTicksLimit: isSmallMobile ? 3 : isMediumMobile ? 4 : undefined,
          },
          grid: {
            display: false,
          },
      },
    },
  };
  };

  const categoryOptions = getCategoryOptions();

  const paymentData = {
    labels: ['Carte Bleue', 'Espèces', 'Mobile', 'Ticket Restaurant'],
    datasets: [
      {
        data: [
          statistics.paymentPercentages.cb_comptoir || 0,
          statistics.paymentPercentages.espece || 0,
          statistics.paymentPercentages.mobile || 0,
          statistics.paymentPercentages.ticket_resto || 0
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(139, 92, 246, 0.8)',
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(139, 92, 246)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const getPaymentOptions = () => {
    const isMobile = window.innerWidth < 768;
    const isSmallMobile = window.innerWidth < 480;
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: isMobile ? 'bottom' : 'top',
          labels: {
            font: {
              size: isSmallMobile ? 10 : 12,
              weight: '600',
            },
            color: '#1f2937',
            padding: isSmallMobile ? 8 : 15,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: isSmallMobile ? 8 : 12,
          borderWidth: 1,
          callbacks: {
            label: (context) => {
              return `${context.label}: ${context.parsed}%`;
            },
          },
        },
      },
    };
  };

  const paymentOptions = getPaymentOptions();

  if (loading) {
    return (
      <div className="stats-main-container">
        <div className="stats-loading-container">
          <div className="stats-loading-spinner"></div>
          <p className="stats-loading-text">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-main-container">
      {/* Header */}
      <div className="stats-page-header">
        <h1 className="stats-page-title">
          <FaChartLine className="stats-title-icon" />
          Statistiques & Analyses
        </h1>
        <p className="stats-page-subtitle">
          Vue d'ensemble des performances de votre restaurant
        </p>
      </div>

      {/* Summary Cards */}
      <div className="stats-cards-grid">
        <div className="stats-summary-card revenue">
          <div className="stats-card-header">
            <h3 className="stats-card-title">Revenus du Jour</h3>
            <FaEuroSign className="stats-card-icon revenue" />
          </div>
          <p className="stats-card-value">{statistics.dailyRevenue.toFixed(2)} €</p>
          <p className="stats-card-label">Aujourd'hui</p>
        </div>

        <div className="stats-summary-card orders">
          <div className="stats-card-header">
            <h3 className="stats-card-title">Commandes du Jour</h3>
            <FaShoppingCart className="stats-card-icon orders" />
          </div>
          <p className="stats-card-value">{statistics.dailyOrders}</p>
          <p className="stats-card-label">Aujourd'hui</p>
        </div>

        <div className="stats-summary-card average">
          <div className="stats-card-header">
            <h3 className="stats-card-title">Moyenne Quotidienne</h3>
            <FaArrowUp className="stats-card-icon average" />
          </div>
          <p className="stats-card-value">{statistics.averageDailyRevenue} €</p>
          <p className="stats-card-label">Par jour</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="stats-charts-section">
        <div className="stats-chart-card">
          <div className="stats-chart-header">
            <h2 className="stats-chart-title">
              <FaUtensils className="stats-chart-icon" />
              Entrées les Plus Commandées
            </h2>
          </div>
          <div className="stats-chart-container">
            <Bar data={entréesData} options={categoryOptions} />
          </div>
        </div>

        <div className="stats-chart-card">
          <div className="stats-chart-header">
            <h2 className="stats-chart-title">
              <FaHamburger className="stats-chart-icon" />
              Plats les Plus Commandés
            </h2>
          </div>
          <div className="stats-chart-container">
            <Bar data={platsData} options={categoryOptions} />
          </div>
        </div>

        <div className="stats-chart-card">
          <div className="stats-chart-header">
            <h2 className="stats-chart-title">
              <FaIceCream className="stats-chart-icon" />
              Desserts les Plus Commandés
            </h2>
          </div>
          <div className="stats-chart-container">
            <Bar data={dessertsData} options={categoryOptions} />
          </div>
        </div>

        <div className="stats-chart-card">
          <div className="stats-chart-header">
            <h2 className="stats-chart-title">
              <FaGlassWhiskey className="stats-chart-icon" />
              Boissons les Plus Commandées
            </h2>
          </div>
          <div className="stats-chart-container">
            <Bar data={boissonsData} options={categoryOptions} />
          </div>
      </div>

        <div className="stats-chart-card">
          <div className="stats-chart-header">
            <h2 className="stats-chart-title">
              <FaClock className="stats-chart-icon" />
              Affluence par Heure (11h - 23h)
            </h2>
          </div>
          <div className="stats-chart-container">
        <Line data={hourlyData} options={hourlyOptions} />
          </div>
      </div>

        <div className="stats-chart-card">
          <div className="stats-chart-header">
            <h2 className="stats-chart-title">
              <FaCreditCard className="stats-chart-icon" />
              Répartition des Moyens de Paiement
            </h2>
          </div>
          <div className="stats-chart-container">
        <Pie data={paymentData} options={paymentOptions} />
          </div>
        </div>
      </div>

      {/* Revenue & Orders Info */}
      <div className="stats-info-grid">
        <div className="stats-info-card">
          <div className="stats-info-header">
            <h2 className="stats-info-title">
              <FaEuroSign className="stats-info-icon revenue" />
              Revenus
            </h2>
          </div>
          <ul className="stats-info-list">
            <li className="stats-info-item">
              <span className="stats-info-label">
                <FaCalendarDay /> Quotidien
              </span>
              <span className="stats-info-value revenue">{statistics.dailyRevenue.toFixed(2)} €</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">
                <FaCalendarWeek /> Hebdomadaire
              </span>
              <span className="stats-info-value revenue">{statistics.weeklyRevenue.toFixed(2)} €</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">
                <FaCalendarAlt /> Mensuel
              </span>
              <span className="stats-info-value revenue">{statistics.monthlyRevenue.toFixed(2)} €</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">Moyenne quotidienne</span>
              <span className="stats-info-value revenue">{statistics.averageDailyRevenue} €</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">Moyenne hebdomadaire</span>
              <span className="stats-info-value revenue">{statistics.averageWeeklyRevenue} €</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">Moyenne mensuelle</span>
              <span className="stats-info-value revenue">{statistics.averageMonthlyRevenue} €</span>
            </li>
          </ul>
        </div>

        <div className="stats-info-card">
          <div className="stats-info-header">
            <h2 className="stats-info-title">
              <FaShoppingCart className="stats-info-icon orders" />
              Commandes
            </h2>
          </div>
          <ul className="stats-info-list">
            <li className="stats-info-item">
              <span className="stats-info-label">
                <FaCalendarDay /> Quotidien
              </span>
              <span className="stats-info-value orders">{statistics.dailyOrders}</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">
                <FaCalendarWeek /> Hebdomadaire
              </span>
              <span className="stats-info-value orders">{statistics.weeklyOrders}</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">
                <FaCalendarAlt /> Mensuel
              </span>
              <span className="stats-info-value orders">{statistics.monthlyOrders}</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">Moyenne quotidienne</span>
              <span className="stats-info-value orders">{statistics.averageDailyOrders}</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">Moyenne hebdomadaire</span>
              <span className="stats-info-value orders">{statistics.averageWeeklyOrders}</span>
            </li>
            <li className="stats-info-item">
              <span className="stats-info-label">Moyenne mensuelle</span>
              <span className="stats-info-value orders">{statistics.averageMonthlyOrders}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
